/**
 * Locale-aware formatters via the Intl API.
 *
 * Fixes the long-standing bug where Hebrew users saw Arabic month names
 * because call sites used `toLocaleDateString(isRTL ? 'ar' : 'en', …)`.
 * Always pass the *actual* i18n language code through to Intl.
 */

const BCP47 = { en: 'en-US', ar: 'ar', he: 'he-IL' };

function toBcp47(lang) {
  if (!lang) return 'en-US';
  const base = String(lang).toLowerCase().split('-')[0];
  return BCP47[base] || lang;
}

function toDate(v) {
  if (v == null) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  // Firestore { _seconds, _nanoseconds } and { seconds, nanoseconds }
  if (typeof v === 'object') {
    const s = typeof v._seconds === 'number' ? v._seconds : v.seconds;
    const n = typeof v._nanoseconds === 'number' ? v._nanoseconds : v.nanoseconds;
    if (typeof s === 'number') {
      const d = new Date(s * 1000 + (n || 0) / 1e6);
      return isNaN(d) ? null : d;
    }
  }
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

const cache = new Map();
function getFormatter(lang, kind, opts) {
  const key = `${kind}|${lang}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (f) return f;
  const bcp = toBcp47(lang);
  f = kind === 'dt' ? new Intl.DateTimeFormat(bcp, opts) : new Intl.NumberFormat(bcp, opts);
  cache.set(key, f);
  return f;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** Deterministic DD/MM/YYYY (local time), slashes, locale-independent. */
export function formatDateOnly(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Deterministic DD/MM/YYYY HH:mm (local time, 24h). */
export function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Default date display is DD/MM/YYYY (the format the centers expect). Callers
 * that pass explicit `opts` (e.g. weekday or month names) still get the
 * locale-aware Intl formatting.
 */
export function formatDate(value, lang, opts) {
  if (!opts) return formatDateOnly(value);
  const d = toDate(value);
  if (!d) return '';
  return getFormatter(lang, 'dt', opts).format(d);
}

export function formatWeekday(value, lang, opts = { weekday: 'short', month: 'short', day: 'numeric' }) {
  return formatDate(value, lang, opts);
}

export function formatTime(value, lang, opts = { hour: '2-digit', minute: '2-digit' }) {
  const d = toDate(value);
  if (!d) return '';
  return getFormatter(lang, 'dt', opts).format(d);
}

export function formatNumber(value, lang, opts) {
  if (value == null || Number.isNaN(+value)) return '';
  return getFormatter(lang, 'num', opts).format(+value);
}

export function formatCurrency(value, lang, currency = 'ILS') {
  if (value == null || Number.isNaN(+value)) return '';
  return getFormatter(lang, 'num', { style: 'currency', currency, maximumFractionDigits: 0 }).format(+value);
}

export function formatPercent(value, lang, opts = { maximumFractionDigits: 0 }) {
  if (value == null || Number.isNaN(+value)) return '';
  return getFormatter(lang, 'num', { ...opts, style: 'percent' }).format(+value);
}

export { toDate };
